import React, { useState, useEffect } from "react";
import { Button } from "antd";
import {
  LeftOutlined,
  ShopOutlined,
  ToolOutlined,
  ShoppingCartOutlined,
  CoffeeOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  DollarOutlined,
} from "@ant-design/icons";

const customStyles = `
  /* Responsive styles for mobile devices */
  @media (max-width: 768px) {
    .mobile-container {
      padding: 20px 16px !important;
    }

    .mobile-card {
      padding: 24px 16px !important;
      margin: 0 !important;
    }

    .mobile-header {
      padding-right: 0 !important;
      margin-bottom: 24px !important;
    }

    .mobile-features-container {
      flex-direction: column !important;
      gap: 8px !important;
    }

    .mobile-feature-column {
      width: 100% !important;
      min-width: 100% !important;
    }

    .mobile-buttons {
      justify-content: center !important;
      margin-top: 24px !important;
      gap: 16px !important;
    }
  }

  /* Desktop styles */
  @media (min-width: 769px) {
    .mobile-features-container {
      flex-direction: row !important;
    }

    .mobile-buttons {
      justify-content: flex-end !important;
      margin-top: 0 !important;
    }
  }
`;

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);

  return matches;
};

const FEATURES = [
  {
    key: "track_inventory",
    title: "Track Inventory",
    desc: "Keep track of your stock levels, purchases, and sales. Enable this if your business deals with physical goods and you need to monitor stock movements, calculate inventory value, or generate inventory reports.",
    icon: <ShopOutlined style={{ fontSize: "20px", color: "#343C46" }} />,
  },
  {
    key: "manufacturing_enabled",
    title: "Enable Manufacturing",
    desc: "Convert raw materials into finished goods. Select this if your business involves production—such as assembling or manufacturing items—and you need to record the use of raw materials and output of finished products.",
    icon: <ToolOutlined style={{ fontSize: "20px", color: "#343C46" }} />,
  },
  {
    key: "pos_retail_enabled",
    title: "Point of Sale (Retail)",
    desc: "Billing system for retail shops. Use this if you run a retail outlet like a store or general shop and need a POS interface for quick billing, accepting payments, and managing counter sales.",
    icon: (
      <ShoppingCartOutlined style={{ fontSize: "20px", color: "#343C46" }} />
    ),
  },
  {
    key: "pos_restaurant_enabled",
    title: "Point of Sale (Restaurant)",
    desc: "Billing system for restaurants, cafes, or food stalls. Select this if you operate a food service business and need features like table billing, KOT (Kitchen Order Ticket), and split payments for dine-in or takeaway.",
    icon: <CoffeeOutlined style={{ fontSize: "20px", color: "#343C46" }} />,
  },
  {
    key: "location_enabled",
    title: "Multiple Locations",
    desc: "Sell from multiple business locations. Choose this if your business operates from more than one branch or store and you want to manage sales, purchases, and inventory separately for each location.",
    icon: (
      <EnvironmentOutlined style={{ fontSize: "20px", color: "#343C46" }} />
    ),
  },
  {
    key: "warehouse_enabled",
    title: "Multiple Warehouses",
    desc: "Track inventory across multiple storage sites. Enable this if you store goods in different warehouses and need to manage stock availability, transfers, and tracking between them.",
    icon: <HomeOutlined style={{ fontSize: "20px", color: "#343C46" }} />,
  },
  {
    key: "multi_currency_enabled",
    title: "Multi-Currency",
    desc: "Handle transactions and bank accounts in multiple currencies. Turn this on if your business operates bank accounts in different currencies and needs to record transactions, view balances, and generate reports in foreign currencies.",
    icon: <DollarOutlined style={{ fontSize: "20px", color: "#343C46" }} />,
  },
];

const Second = ({ formData, setForm, extra, setExtra, navigation }) => {
  const normalizeSelection = (list) => {
    let deduplicated = Array.from(new Set(list));
    const hasPos =
      deduplicated.includes("pos_retail_enabled") ||
      deduplicated.includes("pos_restaurant_enabled");
    const hasLocation = deduplicated.includes("location_enabled");
    if (hasPos && !hasLocation) {
      deduplicated = [...deduplicated, "location_enabled"];
    }
    if (!deduplicated.includes("location_enabled")) {
      deduplicated = deduplicated.filter(
        (k) => k !== "pos_retail_enabled" && k !== "pos_restaurant_enabled",
      );
    }
    const hasManufacturing = deduplicated.includes("manufacturing_enabled");
    const hasWarehouse = deduplicated.includes("warehouse_enabled");
    const hasTrackInventory = deduplicated.includes("track_inventory");
    if ((hasManufacturing || hasWarehouse) && !hasTrackInventory) {
      deduplicated = [...deduplicated, "track_inventory"];
    }
    if (!deduplicated.includes("track_inventory")) {
      deduplicated = deduplicated.filter(
        (k) => k !== "manufacturing_enabled" && k !== "warehouse_enabled",
      );
    }
    return deduplicated;
  };
  const [selected, setSelected] = useState(() =>
    normalizeSelection(formData.features || []),
  );
  useEffect(() => {
    const normalized = normalizeSelection(formData.features || []);
    const selectedSet = new Set(selected);
    const isSame =
      normalized.length === selected.length &&
      normalized.every((k) => selectedSet.has(k));
    if (!isSame) {
      setSelected(normalized);
    }
  }, [formData.features]);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const leftFeatures = FEATURES.slice(0, 4);
  const rightFeatures = FEATURES.slice(4);

  const handleSelect = (key) => {
    let toggled = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key];
    if (key === "location_enabled" && !toggled.includes("location_enabled")) {
      toggled = toggled.filter(
        (k) => k !== "pos_retail_enabled" && k !== "pos_restaurant_enabled",
      );
    }
    if (key === "track_inventory" && !toggled.includes("track_inventory")) {
      toggled = toggled.filter(
        (k) => k !== "manufacturing_enabled" && k !== "warehouse_enabled",
      );
    }
    const normalized = normalizeSelection(toggled);
    setSelected(normalized);
    setForm({ target: { name: "features", value: normalized } });
  };

  const handleNext = () => {
    setForm({ target: { name: "features", value: selected } });

    const featuresMapping = {
      track_inventory: selected.includes("track_inventory"),
      manufacturing_enabled: selected.includes("manufacturing_enabled"),
      pos_enabled:
        selected.includes("pos_retail_enabled") ||
        selected.includes("pos_restaurant_enabled"),
      location_enabled: selected.includes("location_enabled"),
      warehouse_enabled: selected.includes("warehouse_enabled"),
      multi_currency_enabled: selected.includes("multi_currency_enabled"),
      pos_retail_enabled: selected.includes("pos_retail_enabled"),
      pos_restaurant_enabled: selected.includes("pos_restaurant_enabled"),
    };

    setExtra((prevExtra) => ({
      ...prevExtra,
      namespace_features: featuresMapping,
    }));

    navigation.next();
  };

  const renderFeature = (feature, isSelected) => {
    const selectedStyle = isSelected
      ? {
          background: "#F5F8FF",
          outline: "1px var(--Main-blue, #143E9F) solid",
        }
      : {
          background: "white",
          outline: "1px #E0E5EB solid",
        };

    const titleColor = isSelected ? "var(--Main-blue, #143E9F)" : "#343C46";
    const descColor = isSelected ? "#5271B8" : "#636972";

    const checkbox = isSelected ? (
      <div style={{ width: 20, height: 20, position: "relative" }}>
        <div
          style={{
            width: 18,
            height: 18,
            left: 1,
            top: 1,
            position: "absolute",
            background: "var(--Main-blue, #143E9F)",
            borderRadius: 4,
            border: "1px var(--Main-blue, #143E9F) solid",
          }}
        />
        <div
          style={{
            width: 12,
            height: 12,
            left: 3.5,
            top: 4,
            position: "absolute",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: 7.88,
              height: 5.62,
              left: 2.06,
              top: 3.56,
              position: "absolute",
              borderBottom: "2px solid white",
              borderLeft: "2px solid white",
              transform: "rotate(-45deg)",
            }}
          />
        </div>
      </div>
    ) : (
      <div style={{ width: 20, height: 20, position: "relative" }}>
        <div
          style={{
            width: 18,
            height: 18,
            left: 1,
            top: 1,
            position: "absolute",
            background: "white",
            borderRadius: 4,
            border: "1px #969798 solid",
          }}
        />
      </div>
    );

    return (
      <div
        key={feature.key}
        style={{
          alignSelf: "stretch",
          minHeight: 100,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 12,
          paddingBottom: 12,
          borderRadius: 8,
          outlineOffset: "-1px",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 17,
          display: "inline-flex",
          cursor: "pointer",
          ...selectedStyle,
        }}
        onClick={() => handleSelect(feature.key)}
        tabIndex={0}
        role="button"
        aria-pressed={isSelected}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleSelect(feature.key);
        }}
      >
        <div
          style={{
            width: 78,
            alignSelf: "stretch",
            padding: 10.17,
            background: "#F6F8FA",
            borderRadius: 5.09,
            justifyContent: "center",
            alignItems: "center",
            gap: 5.09,
            display: "flex",
          }}
        >
          {feature.icon}
        </div>
        <div
          style={{
            flex: "1 1 0",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: 2,
            display: "inline-flex",
          }}
        >
          <div
            style={{
              alignSelf: "stretch",
              color: titleColor,
              fontSize: 14,
              fontFamily: "Inter",
              fontWeight: 700,
              wordWrap: "break-word",
            }}
          >
            {feature.title}
          </div>
          <div
            style={{
              alignSelf: "stretch",
              color: descColor,
              fontSize: 10,
              fontFamily: "Inter",
              fontWeight: 400,
              wordWrap: "break-word",
            }}
          >
            {feature.desc}
          </div>
        </div>
        {checkbox}
      </div>
    );
  };

  return (
    <div
      className="mobile-container"
      style={{
        backgroundColor: "#EEF2F7",
        minHeight: "100vh",
        padding: isMobile ? "20px 16px" : "96px 192px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <style>{customStyles}</style>

      <div
        className="mobile-card"
        style={{
          backgroundColor: "#FAFAFA",
          borderRadius: "16px",
          boxShadow: "0px 28px 62px rgba(37, 78, 207, 0.03)",
          padding: isMobile ? "24px 16px" : "40px 32px",
          width: "100%",
          maxWidth: "900px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        <div
          className="mobile-header"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: "0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              flex: 1,
              gap: "24px",
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#343C46",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Accounting Features
              </h1>
              <p
                style={{
                  margin: "2px 0 0 0",
                  color: "#636972",
                  fontSize: "12px",
                  fontWeight: "400",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Enjoy additional features with Tigg alongside basic accounting
              </p>
            </div>
          </div>

          <div style={{ width: "70px", height: "40px", position: "relative" }}>
            <img
              src="images/logo.svg"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              alt="Tigg Logo"
            />
          </div>
        </div>

        <div
          className="mobile-features-container"
          style={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <div
            className="mobile-feature-column"
            style={{
              flex: "1 1 0",
              minWidth: isMobile ? "100%" : "320px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {leftFeatures.map((f) =>
              renderFeature(f, selected.includes(f.key)),
            )}
          </div>
          <div
            className="mobile-feature-column"
            style={{
              flex: "1 1 0",
              minWidth: isMobile ? "100%" : "320px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              justifyContent: "space-between",
            }}
          >
            {rightFeatures.map((f) =>
              renderFeature(f, selected.includes(f.key)),
            )}

            <div
              className="mobile-buttons"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                alignItems: "flex-end",
                gap: isMobile ? 12 : 24,
                marginTop: isMobile ? "24px" : "0",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Button
                icon={<LeftOutlined />}
                size="large"
                style={{
                  borderRadius: 4,
                  minWidth: isMobile ? "100%" : 100,
                  fontWeight: 600,
                  fontFamily: "Inter",
                  height: isMobile ? 48 : undefined,
                  fontSize: "12px",
                }}
                onClick={navigation.previous}
              >
                Back
              </Button>
              <Button
                type="primary"
                size="large"
                style={{
                  height: isMobile ? 48 : 72,
                  paddingLeft: 16,
                  paddingRight: 16,
                  background: "var(--Main-blue, #143E9F)",
                  boxShadow: "0px 8px 24px -8px rgba(20, 62, 159, 0.16)",
                  borderRadius: 6,
                  fontWeight: 600,
                  fontFamily: "Inter",
                  fontSize: 14,
                  lineHeight: "21px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: isMobile ? "100%" : undefined,
                }}
                onClick={handleNext}
              >
                Save and Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Second;
